import { currentHost } from '../config/current-host'
import { HOSTS } from '../config/hosts'
import { genericHost } from '../config/hosts/generic'
import {
  addClip,
  addProjectAudioTrack,
  createProject,
  deleteProject,
  saveProjectText,
  updateClipTrim,
  updateProjectAudioTrack,
} from './storage'
import { requestPersistentStorage } from './storage-space'
import { isProjectKind, projectKind } from './types'
import type {
  ClipId,
  ClipRecord,
  Project,
  ProjectAudioRecord,
  ProjectElement,
  ProjectKind,
  ProjectSection,
} from './types'

/**
 * Single-file project backup, used both as a safety net and to move a
 * project between devices or browser origins (storage is per-origin — e.g.
 * an older deploy → kody.video).
 *
 * Format: `KODYVID1` magic, u32 big-endian JSON manifest length, UTF-8 JSON
 * manifest, then every clip's media bytes concatenated in manifest order,
 * then (when present) the background-audio track's bytes. Older app
 * versions ignore the unknown manifest fields and the trailing audio bytes,
 * so music-carrying backups still import there (clips only).
 * Thumbnails are intentionally excluded — the loader regenerates them.
 */
const MAGIC = 'KODYVID1'
const MAGIC_BYTES = new TextEncoder().encode(MAGIC)

interface ManifestClip {
  /** LOCAL clip id, so elements and the clip order can point at it. */
  id?: string
  mimeType: string
  /** 'picture' for a still; absent reads as video. */
  mediaKind?: string
  durationMs: number
  trimStartMs: number
  trimEndMs: number
  createdAt: number
  width?: number
  height?: number
  lat?: number
  lng?: number
  locationAccuracyM?: number
  /** The clip's own (foreground) sound level override (0–1). */
  clipVolume?: number
  /** Background-music level override (0–1) while this clip plays. */
  musicVolume?: number
  /** Measured whole-clip audio peak (0–1) — restored so imported clips
   * skip the normalization re-measure on their first load. */
  audioPeak?: number
  /** Byte length of this clip's media in the blob section. */
  byteLength: number
}

interface ManifestAudioTrack {
  /** LOCAL track id. */
  id?: string
  mimeType: string
  durationMs: number
  name: string
  /** The words THIS take was read from. */
  script?: string
  addedAt?: number
  /** Output position, and the scenes it is pinned to (LOCAL element ids). */
  startMs?: number
  role?: string
  spanFromElementId?: string
  spanToElementId?: string
  /** Per-track playback settings (absent = whole track, full level,
   * playlist-default fades). Older app versions ignore these. */
  trimStartMs?: number
  trimEndMs?: number
  volume?: number
  fadeIn?: boolean
  fadeOut?: boolean
  /** Byte length of this track, appended after every clip's media bytes
   * (tracks follow in playlist order). */
  byteLength: number
}

interface ManifestAudio {
  fadeIn: boolean
  fadeOut: boolean
  tracks: ManifestAudioTrack[]
}

/**
 * Everything about a project that is not media bytes.
 *
 * Ids in here are LOCAL to the file: they are whatever the install that wrote
 * it happened to use, kept only so the references between them survive. The
 * importer mints fresh ids and rewrites every reference through a map, because
 * two installs will collide otherwise — and because a project imported twice
 * onto one device must be two projects, not one project fighting itself.
 */
interface ManifestDocument {
  sections: ManifestSection[]
  elements: ManifestElement[]
}

interface ManifestSection {
  id: string
  name: string
  notesMd?: string
  sort: number
  createdAt: number
}

interface ManifestElement {
  id: string
  sectionId: string
  kind: string
  sort: number
  /** LOCAL clip ids. */
  clipIds: string[]
  layout?: string
  /** LOCAL element ids — a collage's cells. */
  itemElementIds?: string[]
  contentMd?: string
  notesMd?: string
  hiddenFromFilm?: boolean
  durationMs?: number
  cardBackground?: unknown
  subtitles?: unknown[]
  overlay?: unknown
  createdAt: number
}

interface ManifestV2 extends Omit<Manifest, 'version'> {
  version: 2
  /** The product and build that wrote it. Diagnostics only. */
  writtenBy?: string
  /** The document: sections and elements. Absent on a legacy flat project. */
  document?: ManifestDocument
  /** Caption blocks, whose element refs are LOCAL ids. */
  captions?: unknown[]
  subtitleStyle?: unknown
  pictureHoldMs?: number
  nameIsDefault?: boolean
  createdAt?: number
  /** The project's clip order, as LOCAL clip ids. */
  clipOrder?: string[]
}

interface Manifest {
  version: 1
  app: 'kody-video'
  exportedAt: number
  projectName: string
  /** Which tab the project belongs to. Absent in backups written before
   * voice/text existed — and in those, every project was a video one, so a
   * missing value reads as 'video'. Older app versions ignore the field and
   * restore the clips as a video project, which is the only thing they can
   * do with them anyway. */
  projectKind?: ProjectKind
  /** The project's body copy (a text note, or a voice project's notes). */
  body?: string
  clips: ManifestClip[]
  /** Background-music playlist (absent on projects without one). */
  audio?: ManifestAudio
}

/**
 * The extension THIS host writes. Per-host (see `HostStorage`), because a
 * backup a user finds in their downloads should carry the name of the product
 * that made it.
 */
export function backupExtension(): string {
  return currentHost().storage.backupExtension
}

/** Upstream Kody Video backups use the same wire format — keep importing them. */
export const LEGACY_BACKUP_EXTENSION = '.kodyvideo'

/**
 * Extensions written by deployments that no longer exist.
 *
 * **Listed explicitly, and never derived from `HOSTS`.** They used to be — and
 * that was a trap: retiring a host would have silently removed its extension
 * from the file picker, so the backup files its users had just been told to
 * export would show as unselectable. The files outlive the deployment, which
 * is the entire point of an export, so the read list has to outlive it too.
 *
 * Nothing may be removed from this list. A file on someone's phone does not
 * expire because we tidied up.
 */
export const RETIRED_BACKUP_EXTENSIONS = [
  // me.o-kb.com and cam.o-kb.com, retired once everyone moved to o-cam.app.
  '.okbvideo',
  // cam.console.openside.com, retired at the same time.
  '.consolevideo',
] as const

/**
 * Every extension this build recognises on the way IN: this host's, every
 * retired one, the unbranded fallback's, and the legacy one.
 *
 * Deliberately not just the current host's. The wire format is identical
 * everywhere, so a user restoring a project exported from a deployment that
 * has since been switched off — the exact case backups exist for — must not be
 * told their own file is unrecognised. Writing stays single-extension; only
 * reading is generous.
 */
export function knownBackupExtensions(): string[] {
  const all = [
    backupExtension(),
    ...HOSTS.map((host) => host.storage.backupExtension),
    ...RETIRED_BACKUP_EXTENSIONS,
    genericHost.storage.backupExtension,
    LEGACY_BACKUP_EXTENSION,
  ]
  return [...new Set(all)]
}

export function projectBackupFilename(projectName: string): string {
  const slug =
    projectName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .slice(0, 40) || 'project'
  return `${slug}${backupExtension()}`
}

/**
 * Extensions a file picker should suggest. A HINT, not a gate: `accept` is
 * advisory, and a deliberately picked file is imported whatever it is called
 * (see `importKodyVideoBackupFile`'s callers) because a backup that came off a
 * phone through a share sheet or a download often arrives renamed.
 */
export function backupFileAccept(): string {
  return [...knownBackupExtensions(), 'application/zip'].join(',')
}

/** True when the picked/dropped file uses a recognized backup extension. */
export function isKodyVideoBackupFile(file: Pick<File, 'name'>): boolean {
  const name = file.name.toLowerCase()
  return knownBackupExtensions().some((extension) => name.endsWith(extension))
}

/**
 * The progress line shown while a backup restores.
 *
 * Shared by the drop target and the picker so the two entry points cannot drift
 * into describing the same operation differently.
 */
export function backupImportProgressLabel(doneClips: number, totalClips: number): string {
  if (totalClips <= 0) return 'Reading backup…'
  return `Importing clip ${Math.min(doneClips + 1, totalClips)} of ${totalClips}…`
}

/** The first line of that progress, before any clip count is known. */
export const BACKUP_IMPORT_READING = 'Reading backup…'

/**
 * A user-ready message for a failed import.
 *
 * `BackupFormatError` already carries a sentence written for the user; anything
 * else is a bug or a broken file and gets a plain fallback rather than an
 * internal message.
 */
export function backupImportErrorMessage(err: unknown): string {
  if (err instanceof BackupFormatError) return err.message
  return err instanceof Error && err.message ? err.message : 'Could not import that file'
}

/** Backup files from a picker or drop, in the order they were supplied. */
export function kodyVideoBackupFilesFromList(
  files: Iterable<File> | ArrayLike<File> | null | undefined,
): File[] {
  if (!files) return []
  return Array.from(files).filter(isKodyVideoBackupFile)
}

/** True when a drag payload may contain OS files (names are hidden until drop). */
export function dataTransferHasFiles(dataTransfer: DataTransfer | null | undefined): boolean {
  return Boolean(dataTransfer?.types.includes('Files'))
}

/** Bundle a project into one shareable/downloadable backup Blob. */
export function serializeProject(
  project: Project,
  clips: ClipRecord[],
  audio?: ProjectAudioRecord | null,
  /** The project's body copy, when it has one. */
  body?: string | null,
  /**
   * The document — sections and elements.
   *
   * Optional only so a caller with a legacy flat project can omit it. A caller
   * that HAS one and leaves it out writes a backup that loses the whole
   * arrangement, which is the bug this format exists to fix; pass it.
   */
  document?: { sections: readonly ProjectSection[]; elements: readonly ProjectElement[] } | null,
): Blob {
  const manifest: ManifestV2 = {
    version: 2,
    app: 'kody-video',
    exportedAt: Date.now(),
    writtenBy: currentHost().brand.productName,
    projectName: project.name,
    projectKind: projectKind(project),
    createdAt: project.createdAt,
    ...(project.nameIsDefault ? { nameIsDefault: true } : {}),
    ...(project.subtitleStyle ? { subtitleStyle: project.subtitleStyle } : {}),
    ...(project.pictureHoldMs !== undefined ? { pictureHoldMs: project.pictureHoldMs } : {}),
    ...(project.captions?.length ? { captions: project.captions } : {}),
    ...(project.clipIds?.length ? { clipOrder: [...project.clipIds] } : {}),
    ...(document && (document.sections.length > 0 || document.elements.length > 0)
      ? {
          document: {
            sections: document.sections.map((section) => ({
              id: section.id,
              name: section.name,
              ...(section.notesMd ? { notesMd: section.notesMd } : {}),
              sort: section.sort,
              createdAt: section.createdAt,
            })),
            elements: document.elements.map((element) => ({
              id: element.id,
              sectionId: element.sectionId,
              kind: element.kind,
              sort: element.sort,
              clipIds: [...element.clipIds],
              ...(element.layout ? { layout: element.layout } : {}),
              ...(element.itemElementIds?.length
                ? { itemElementIds: [...element.itemElementIds] }
                : {}),
              ...(element.contentMd !== undefined ? { contentMd: element.contentMd } : {}),
              ...(element.notesMd !== undefined ? { notesMd: element.notesMd } : {}),
              ...(element.hiddenFromFilm ? { hiddenFromFilm: true } : {}),
              ...(element.durationMs !== undefined ? { durationMs: element.durationMs } : {}),
              ...(element.cardBackground ? { cardBackground: element.cardBackground } : {}),
              ...(element.subtitles?.length ? { subtitles: element.subtitles } : {}),
              ...(element.overlay ? { overlay: element.overlay } : {}),
              createdAt: element.createdAt,
            })),
          },
        }
      : {}),
    ...(body ? { body } : {}),
    clips: clips.map((clip) => ({
      id: clip.id,
      ...(clip.mediaKind ? { mediaKind: clip.mediaKind } : {}),
      mimeType: clip.mimeType,
      durationMs: clip.durationMs,
      trimStartMs: clip.trimStartMs,
      trimEndMs: clip.trimEndMs,
      createdAt: clip.createdAt,
      width: clip.width,
      height: clip.height,
      lat: clip.lat,
      lng: clip.lng,
      locationAccuracyM: clip.locationAccuracyM,
      clipVolume: clip.clipVolume,
      musicVolume: clip.musicVolume,
      audioPeak: clip.audioPeak,
      byteLength: clip.blob.size,
    })),
  }
  if (audio && audio.tracks.length > 0) {
    manifest.audio = {
      fadeIn: audio.fadeIn,
      fadeOut: audio.fadeOut,
      tracks: audio.tracks.map((track) => ({
        id: track.id,
        mimeType: track.mimeType,
        durationMs: track.durationMs,
        name: track.name,
        ...(track.script ? { script: track.script } : {}),
        addedAt: track.addedAt,
        ...(track.startMs !== undefined ? { startMs: track.startMs } : {}),
        ...(track.role ? { role: track.role } : {}),
        ...(track.spanFromElementId ? { spanFromElementId: track.spanFromElementId } : {}),
        ...(track.spanToElementId ? { spanToElementId: track.spanToElementId } : {}),
        trimStartMs: track.trimStartMs,
        trimEndMs: track.trimEndMs,
        volume: track.volume,
        fadeIn: track.fadeIn,
        fadeOut: track.fadeOut,
        byteLength: track.blob.size,
      })),
    }
  }

  const manifestBytes = new TextEncoder().encode(JSON.stringify(manifest))
  const header = new Uint8Array(MAGIC_BYTES.byteLength + 4)
  header.set(MAGIC_BYTES, 0)
  new DataView(header.buffer).setUint32(MAGIC_BYTES.byteLength, manifestBytes.byteLength)

  // Blob composition references the clip blobs — nothing is copied here.
  return new Blob(
    [
      header,
      manifestBytes,
      ...clips.map((clip) => clip.blob),
      ...(audio?.tracks.map((track) => track.blob) ?? []),
    ],
    { type: 'application/octet-stream' },
  )
}

export interface ParsedBackupAudio {
  fadeIn: boolean
  fadeOut: boolean
  tracks: Array<Omit<ManifestAudioTrack, 'byteLength'> & { blob: Blob; localId?: string }>
}

/** The document as the file states it: LOCAL ids, remapped on import. */
export interface ParsedDocument {
  sections: ManifestSection[]
  elements: ManifestElement[]
}

export interface ParsedBackup {
  /** Sections and elements. Null for a v1 file, which carried neither. */
  document: ParsedDocument | null
  /** Caption blocks, element refs still LOCAL. */
  captions: unknown[]
  subtitleStyle: unknown
  pictureHoldMs: number | undefined
  nameIsDefault: boolean
  createdAt: number | undefined
  /** The project's clip order, as LOCAL clip ids. */
  clipOrder: string[]
  projectName: string
  /** Which tab to restore into ('video' for pre-kind backups). */
  projectKind: ProjectKind
  /** The project's body copy ('' when the backup carries none). */
  body: string
  clips: Array<Omit<ManifestClip, 'byteLength'> & { blob: Blob; localId?: string }>
  /** Background-music playlist, when the backup carries one. */
  audio: ParsedBackupAudio | null
}

/**
 * A file the user picked that isn't a (valid, current) project backup.
 * Surfaced in-app as guidance; expected user input, never a crash report.
 */
export class BackupFormatError extends Error {
  override readonly name = 'BackupFormatError'
}

/**
 * Parse a backup file. Media bytes are sliced lazily per clip (File.slice),
 * so large backups never need the whole file in memory at once.
 */
export async function parseProjectBackup(file: Blob): Promise<ParsedBackup> {
  const headerLength = MAGIC_BYTES.byteLength + 4
  if (file.size < headerLength) throw new BackupFormatError('Not a recognized project backup file')

  const header = new Uint8Array(await file.slice(0, headerLength).arrayBuffer())
  for (let i = 0; i < MAGIC_BYTES.byteLength; i += 1) {
    if (header[i] !== MAGIC_BYTES[i]) throw new BackupFormatError('Not a recognized project backup file')
  }
  const manifestLength = new DataView(header.buffer).getUint32(MAGIC_BYTES.byteLength)
  if (manifestLength <= 0 || headerLength + manifestLength > file.size) {
    throw new BackupFormatError('This backup file is damaged')
  }

  let manifest: Manifest | ManifestV2
  try {
    const manifestBytes = await file
      .slice(headerLength, headerLength + manifestLength)
      .arrayBuffer()
    manifest = JSON.parse(new TextDecoder().decode(manifestBytes)) as Manifest | ManifestV2
  } catch {
    throw new BackupFormatError('This backup file is damaged')
  }
  // v1 and v2 share a container and a clip section; v2 adds the document and
  // the project's own settings. Reading both is the whole point — a user's
  // downloads folder has years of v1 files in it.
  const version: number = manifest.version
  if (manifest.app !== 'kody-video' || (version !== 1 && version !== 2) || !Array.isArray(manifest.clips)) {
    throw new BackupFormatError('This backup was made by a newer app version — update and retry')
  }

  let offset = headerLength + manifestLength
  const clips: ParsedBackup['clips'] = []
  for (const clip of manifest.clips) {
    if (
      !Number.isInteger(clip.byteLength) ||
      clip.byteLength <= 0 ||
      offset + clip.byteLength > file.size
    ) {
      throw new BackupFormatError('This backup file is damaged')
    }
    const mimeType = typeof clip.mimeType === 'string' ? clip.mimeType : 'video/webm'
    clips.push({
      mimeType,
      durationMs: clip.durationMs,
      trimStartMs: clip.trimStartMs,
      trimEndMs: clip.trimEndMs,
      createdAt: clip.createdAt,
      width: clip.width,
      height: clip.height,
      lat: clip.lat,
      lng: clip.lng,
      locationAccuracyM: clip.locationAccuracyM,
      // Old backups carried an `audioVolume` mix share instead — ignored.
      localId: typeof clip.id === 'string' ? clip.id : undefined,
      mediaKind: clip.mediaKind === 'picture' ? ('picture' as const) : undefined,
      clipVolume: typeof clip.clipVolume === 'number' ? clip.clipVolume : undefined,
      musicVolume: typeof clip.musicVolume === 'number' ? clip.musicVolume : undefined,
      audioPeak:
        typeof clip.audioPeak === 'number' && Number.isFinite(clip.audioPeak)
          ? Math.max(0, Math.min(1, clip.audioPeak))
          : undefined,
      blob: file.slice(offset, offset + clip.byteLength, mimeType),
    })
    offset += clip.byteLength
  }

  const finiteOrUndefined = (value: unknown): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined

  let audio: ParsedBackup['audio'] = null
  const manifestAudio = manifest.audio
  if (manifestAudio && Array.isArray(manifestAudio.tracks) && manifestAudio.tracks.length > 0) {
    const tracks: ParsedBackupAudio['tracks'] = []
    for (const track of manifestAudio.tracks) {
      if (
        !Number.isInteger(track.byteLength) ||
        track.byteLength <= 0 ||
        offset + track.byteLength > file.size ||
        !Number.isFinite(track.durationMs) ||
        track.durationMs <= 0
      ) {
        throw new BackupFormatError('This backup file is damaged')
      }
      const mimeType = typeof track.mimeType === 'string' ? track.mimeType : 'audio/mpeg'
      tracks.push({
        localId: typeof track.id === 'string' ? track.id : undefined,
        mimeType,
        durationMs: track.durationMs,
        name: String(track.name || 'Audio track'),
        script: typeof track.script === 'string' ? track.script : undefined,
        addedAt: finiteOrUndefined(track.addedAt),
        startMs: finiteOrUndefined(track.startMs),
        role: track.role === 'voice' ? ('voice' as const) : track.role === 'music' ? ('music' as const) : undefined,
        spanFromElementId:
          typeof track.spanFromElementId === 'string' ? track.spanFromElementId : undefined,
        spanToElementId:
          typeof track.spanToElementId === 'string' ? track.spanToElementId : undefined,
        trimStartMs: finiteOrUndefined(track.trimStartMs),
        trimEndMs: finiteOrUndefined(track.trimEndMs),
        volume: finiteOrUndefined(track.volume),
        fadeIn: typeof track.fadeIn === 'boolean' ? track.fadeIn : undefined,
        fadeOut: typeof track.fadeOut === 'boolean' ? track.fadeOut : undefined,
        blob: file.slice(offset, offset + track.byteLength, mimeType),
      })
      offset += track.byteLength
    }
    audio = {
      fadeIn: manifestAudio.fadeIn !== false,
      fadeOut: manifestAudio.fadeOut !== false,
      tracks,
    }
  }

  const v2 = version === 2 ? (manifest as ManifestV2) : null
  const doc = v2?.document
  return {
    document:
      doc && Array.isArray(doc.sections) && Array.isArray(doc.elements)
        ? { sections: doc.sections, elements: doc.elements }
        : null,
    captions: Array.isArray(v2?.captions) ? v2.captions : [],
    subtitleStyle: v2?.subtitleStyle ?? undefined,
    pictureHoldMs: finiteOrUndefined(v2?.pictureHoldMs),
    nameIsDefault: v2?.nameIsDefault === true,
    createdAt: finiteOrUndefined(v2?.createdAt),
    clipOrder: Array.isArray(v2?.clipOrder) ? v2.clipOrder.filter((id) => typeof id === 'string') : [],
    projectName: String(manifest.projectName || 'Imported project'),
    projectKind: isProjectKind(manifest.projectKind) ? manifest.projectKind : 'video',
    body: typeof manifest.body === 'string' ? manifest.body : '',
    clips,
    audio,
  }
}

let importLock: Promise<void> = Promise.resolve()

/** Test-only: drop a hung lock so browser-mode cases start independent. */
export function __resetProjectTransferForTests(): void {
  importLock = Promise.resolve()
}

async function withImportLock<T>(run: () => Promise<T>): Promise<T> {
  const previous = importLock
  let release!: () => void
  importLock = new Promise<void>((resolve) => {
    release = resolve
  })
  await previous
  try {
    return await run()
  } finally {
    release()
  }
}

function assertImportableClip(clip: ParsedBackup['clips'][number]): void {
  const finite =
    Number.isFinite(clip.durationMs) &&
    Number.isFinite(clip.trimStartMs) &&
    Number.isFinite(clip.trimEndMs) &&
    Number.isFinite(clip.createdAt)
  // A STILL has no duration, and this used to demand one — so any project
  // holding a photograph failed to import at all, rolled back, and reported a
  // damaged file. The guard predates pictures; only moving footage has to run
  // for longer than nothing.
  const isStill = clip.mediaKind === 'picture'
  if (!finite || (!isStill && clip.durationMs <= 0) || clip.blob.size <= 0) {
    throw new BackupFormatError('This backup file is damaged')
  }
}

/**
 * Parse a backup file and persist it as a new project. Requests persistent
 * storage on success (same as the About picker / drop import paths).
 */
export async function importKodyVideoBackupFile(
  file: File,
  onProgress?: (doneClips: number, totalClips: number) => void,
): Promise<Project> {
  const parsed = await parseProjectBackup(file)
  const project = await importProjectBackup(parsed, onProgress)
  requestPersistentStorage()
  return project
}

/** Create a fresh project (new ids) from a parsed backup. */
export async function importProjectBackup(
  parsed: ParsedBackup,
  onProgress?: (doneClips: number, totalClips: number) => void,
): Promise<Project> {
  return withImportLock(() => persistImportedProject(parsed, onProgress))
}

async function persistImportedProject(
  parsed: ParsedBackup,
  onProgress?: (doneClips: number, totalClips: number) => void,
): Promise<Project> {
  const project = await createProject({ name: parsed.projectName, kind: parsed.projectKind })
  /** LOCAL clip id → the id this device just minted for it. */
  const clipIdByLocal = new Map<string, ClipId>()
  /** LOCAL track id → the id this device just minted for it. */
  const trackIdByLocal = new Map<string, string>()
  try {
    if (parsed.body) await saveProjectText(project.id, parsed.body)
    let done = 0
    onProgress?.(0, parsed.clips.length)
    for (const clip of parsed.clips) {
      assertImportableClip(clip)
      // CRITICAL: materialize the media bytes. The parsed blob is a lazy
      // slice of the picked backup File; persisting that into IndexedDB
      // stores a reference to the underlying file, which goes stale (esp.
      // Android content URIs) and leaves clips unreadable. Reading it here
      // both copies the bytes and proves the file is intact.
      const bytes = await clip.blob.arrayBuffer()
      const added = await addClip({
        projectId: project.id,
        blob: new Blob([bytes], { type: clip.mimeType }),
        mimeType: clip.mimeType,
        durationMs: clip.durationMs,
        // Keep the original capture time so chapter titles stay truthful.
        createdAt: clip.createdAt,
        width: clip.width,
        height: clip.height,
        lat: clip.lat,
        lng: clip.lng,
        locationAccuracyM: clip.locationAccuracyM,
        clipVolume: clip.clipVolume,
        musicVolume: clip.musicVolume,
        audioPeak: clip.audioPeak,
        ...(clip.mediaKind === 'picture' ? { mediaKind: 'picture' as const } : {}),
      })
      if (clip.localId) clipIdByLocal.set(clip.localId, added.id)
      // Restore trims (addClip resets them to the full clip).
      const trimmed = await updateClipTrim(added.id, clip.trimStartMs, clip.trimEndMs)
      // Generate thumbnails now so the slot poster shows right away and the
      // first open doesn't pay the backfill cost. Lazy import keeps mediabunny
      // out of the home-shell graph until an import actually runs. Voice
      // takes have no video track to poster.
      if (parsed.projectKind === 'video' && clip.mediaKind !== 'picture') {
        const { ensureClipThumbs } = await import('./thumbs')
        await ensureClipThumbs({ ...added, ...trimmed, blob: added.blob }).catch(() => undefined)
      }
      done += 1
      onProgress?.(done, parsed.clips.length)
    }
    // Any track failure fails the import like a clip failure would, so the
    // rollback below never leaves half the music behind.
    if (parsed.audio) {
      for (const track of parsed.audio.tracks) {
        const bytes = await track.blob.arrayBuffer()
        const record = await addProjectAudioTrack({
          projectId: project.id,
          blob: new Blob([bytes], { type: track.mimeType }),
          mimeType: track.mimeType,
          durationMs: track.durationMs,
          name: track.name,
          ...(track.role === 'voice' || track.role === 'music' ? { role: track.role } : {}),
          // Playlist settings land with the first track; later adds keep them.
          fadeIn: parsed.audio.fadeIn,
          fadeOut: parsed.audio.fadeOut,
        })
        const addedTrack = record.tracks.at(-1)!
        if (track.localId) trackIdByLocal.set(track.localId, addedTrack.id)
        // Restore the track's own playback settings (trim, level, fades).
        if (
          track.trimStartMs !== undefined ||
          track.trimEndMs !== undefined ||
          track.volume !== undefined ||
          track.fadeIn !== undefined ||
          track.fadeOut !== undefined
        ) {
          await updateProjectAudioTrack(project.id, record.tracks.at(-1)!.id, {
            trimStartMs: track.trimStartMs,
            trimEndMs: track.trimEndMs,
            volume: track.volume,
            fadeIn: track.fadeIn,
            fadeOut: track.fadeOut,
          })
        }
      }
    }
    await restoreDocument(project, parsed, clipIdByLocal, trackIdByLocal)
    return project
  } catch (error) {
    // Never leave a half-imported project behind.
    await deleteProject(project.id).catch(() => undefined)
    throw error
  }
}


/**
 * Rebuild the document, the captions and the track pinning after the media.
 *
 * Everything in the file points at everything else by the ids the SOURCE
 * install used, and this device has just minted its own for every clip and
 * every track. So each reference is rewritten through a map, and a reference
 * that cannot be resolved is dropped rather than carried as a dangling id — a
 * caption pinned to an element that does not exist renders nowhere and is
 * impossible to find and delete, which is worse than a caption that simply did
 * not survive.
 *
 * Runs after the clips and the audio, because it needs both maps.
 */
async function restoreDocument(
  project: Project,
  parsed: ParsedBackup,
  clipIdByLocal: ReadonlyMap<string, ClipId>,
  trackIdByLocal: ReadonlyMap<string, string>,
): Promise<void> {
  const {
    putProjectDocument,
    setProjectAudioScript,
    setProjectAudioSpan,
    setProjectCaptions,
    setProjectSubtitleStyle,
    updateProjectAudioTrack,
  } = await import('./storage')

  const now = Date.now()
  /** LOCAL element/section id → the id written here. */
  const sectionIdByLocal = new Map<string, string>()
  const elementIdByLocal = new Map<string, string>()

  if (parsed.document) {
    for (const section of parsed.document.sections) {
      sectionIdByLocal.set(section.id, newId('sec'))
    }
    for (const element of parsed.document.elements) {
      elementIdByLocal.set(element.id, newId('el'))
    }

    const sections = parsed.document.sections.map((section) => ({
      id: sectionIdByLocal.get(section.id)!,
      projectId: project.id,
      name: section.name,
      ...(section.notesMd ? { notesMd: section.notesMd } : {}),
      sort: section.sort,
      createdAt: section.createdAt,
    })) as ProjectSection[]

    const elements = parsed.document.elements
      .map((element) => {
        const sectionId = sectionIdByLocal.get(element.sectionId)
        // An element whose section did not survive has nowhere to live.
        if (!sectionId) return null
        return {
          id: elementIdByLocal.get(element.id)!,
          projectId: project.id,
          sectionId,
          kind: element.kind,
          sort: element.sort,
          clipIds: element.clipIds
            .map((local) => clipIdByLocal.get(local))
            .filter((id): id is ClipId => id !== undefined),
          ...(element.layout ? { layout: element.layout } : {}),
          ...(element.itemElementIds?.length
            ? {
                itemElementIds: element.itemElementIds
                  .map((local) => elementIdByLocal.get(local))
                  .filter((id): id is string => id !== undefined),
              }
            : {}),
          ...(element.contentMd !== undefined ? { contentMd: element.contentMd } : {}),
          ...(element.notesMd !== undefined ? { notesMd: element.notesMd } : {}),
          ...(element.hiddenFromFilm ? { hiddenFromFilm: true } : {}),
          ...(element.durationMs !== undefined ? { durationMs: element.durationMs } : {}),
          ...(element.cardBackground
            ? { cardBackground: remapCardBackground(element.cardBackground, clipIdByLocal) }
            : {}),
          ...(element.subtitles?.length ? { subtitles: element.subtitles } : {}),
          ...(element.overlay ? { overlay: element.overlay } : {}),
          createdAt: element.createdAt,
          updatedAt: now,
        }
      })
      .filter((element): element is NonNullable<typeof element> => element !== null) as ProjectElement[]

    await putProjectDocument(project.id, { sections, elements })
  }

  if (parsed.pictureHoldMs !== undefined) {
    const { setProjectPictureHold } = await import('./storage')
    await setProjectPictureHold(project.id, parsed.pictureHoldMs).catch(() => undefined)
  }

  if (parsed.subtitleStyle) {
    await setProjectSubtitleStyle(project.id, parsed.subtitleStyle as never).catch(() => undefined)
  }

  if (parsed.captions.length > 0) {
    const captions = parsed.captions
      .map((raw) => {
        const block = raw as { id?: string; fromElementId?: string; toElementId?: string; text?: string; lineMs?: number }
        const from = block.fromElementId ? elementIdByLocal.get(block.fromElementId) : undefined
        const to = block.toElementId ? elementIdByLocal.get(block.toElementId) : undefined
        if (!from || !to) return null
        return {
          id: newId('cap'),
          fromElementId: from,
          toElementId: to,
          text: String(block.text ?? ''),
          ...(typeof block.lineMs === 'number' ? { lineMs: block.lineMs } : {}),
        }
      })
      .filter((block): block is NonNullable<typeof block> => block !== null)
    if (captions.length > 0) {
      await setProjectCaptions(project.id, captions as never).catch(() => undefined)
    }
  }

  // A take's scenes, rewritten to the elements that just landed. `startMs` is
  // reconciled from the span by the app on the next commit, so a span that
  // survived is enough — but it is written too, so a take is in the right place
  // before anything is edited.
  for (const track of parsed.audio?.tracks ?? []) {
    const id = track.localId ? trackIdByLocal.get(track.localId) : undefined
    if (!id) continue
    if (track.startMs !== undefined) {
      await updateProjectAudioTrack(project.id, id, { startMs: track.startMs }).catch(
        () => undefined,
      )
    }
    if (track.script) {
      await setProjectAudioScript(project.id, id, track.script).catch(() => undefined)
    }
    const from = track.spanFromElementId ? elementIdByLocal.get(track.spanFromElementId) : undefined
    const to = track.spanToElementId ? elementIdByLocal.get(track.spanToElementId) : undefined
    if (from && to) {
      await setProjectAudioSpan(project.id, id, {
        fromElementId: from,
        toElementId: to,
      }).catch(() => undefined)
    }
  }
}

/** A card's background may name a project picture, whose id has changed. */
function remapCardBackground(
  background: unknown,
  clipIdByLocal: ReadonlyMap<string, ClipId>,
): unknown {
  const ground = background as { clipId?: string } | null
  if (!ground || typeof ground !== 'object') return background
  if (!ground.clipId) return background
  const mapped = clipIdByLocal.get(ground.clipId)
  // The colour and scrim survive even when the picture did not.
  const { clipId: _drop, ...rest } = ground
  return mapped ? { ...rest, clipId: mapped } : rest
}

/** A fresh local id. Matches the shape the app's own stores use. */
function newId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`
}
